import { create } from 'zustand'

export type MaintenanceState = 'off' | 'scheduled' | 'active'

export interface MaintenanceInfo {
  state: MaintenanceState
  message?: string
  startsAt?: string
  endsAt?: string
}

interface MaintenanceStore extends MaintenanceInfo {
  /** Set when a request was refused, so the lockout survives a failed poll. */
  lockedOut: boolean
  dismissedBanner: boolean
  setFromResponse: (info: MaintenanceInfo) => void
  lockOut: (info: MaintenanceInfo) => void
  clear: () => void
  dismissBanner: () => void
}

export const useMaintenanceStore = create<MaintenanceStore>((set) => ({
  state: 'off',
  message: undefined,
  startsAt: undefined,
  endsAt: undefined,
  lockedOut: false,
  dismissedBanner: false,

  /** Result of polling the public endpoint. Clears a lockout when it is over. */
  setFromResponse: (raw) => {
    // The API already reports a finished scheduled window as off; this guards
    // against an older API, so nobody is warned about maintenance that is over.
    // An active window is left alone: only an operator ends an outage.
    const ended =
      raw.state === 'scheduled' && raw.endsAt !== undefined && Date.parse(raw.endsAt) < Date.now()
    const info: MaintenanceInfo = ended ? { state: 'off' } : raw
    set({
      state: info.state,
      message: info.message,
      startsAt: info.startsAt,
      endsAt: info.endsAt,
      lockedOut: info.state === 'active',
      // A new window is a new notice, so a previous dismissal should not hide it.
      ...(info.state === 'off' ? { dismissedBanner: false } : {}),
    })
  },

  /** A request came back 503. Trust it immediately rather than waiting to poll. */
  lockOut: (info) =>
    set({
      state: 'active',
      message: info.message,
      startsAt: info.startsAt,
      endsAt: info.endsAt,
      lockedOut: true,
    }),

  clear: () => set({ state: 'off', lockedOut: false, message: undefined, dismissedBanner: false }),
  dismissBanner: () => set({ dismissedBanner: true }),
}))
